import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "./functions/GlobalContext"
import {
    Box, Text, Center, Spinner, Button,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Stat, StatLabel, StatNumber,
    SimpleGrid, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Link,
} from "@chakra-ui/react"
import { ChevronRightIcon } from '@chakra-ui/icons'
import InfoModal from "./modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "./functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// level: 0=Country, 1=State, 2=City, 3=Cabang, 4=Ranting
const LEVEL_NAMES = ["Country", "State", "City", "Cabang/ Kecamatan", "Ranting"]

type MedalStat = {
    id: number
    name: string
    gold_count: number
    silver_count: number
    bronze_count: number
}

export default function MedalStats() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [level, setLevel] = useState(0)
    const [stats, setStats] = useState<MedalStat[]>([])
    const [loading, setLoading] = useState(true)

    // Drill-down context
    const [countryId, setCountryId] = useState(0)
    const [countryName, setCountryName] = useState("")
    const [stateId, setStateId] = useState(0)
    const [stateName, setStateName] = useState("")
    const [cityId, setCityId] = useState(0)
    const [cityName, setCityName] = useState("")
    const [cabangId, setCabangId] = useState(0)
    const [cabangName, setCabangName] = useState("")

    const [modalData, setModalData] = useState({ title: "", main: "", isOpen: false })
    const closeModal = () => setModalData({ ...modalData, isOpen: false })

    const token = globalContext?.GetLoginData().token

    const loadStats = async () => {
        setLoading(true)
        let parentId = 0
        if (level == 1) parentId = countryId
        else if (level == 2) parentId = stateId
        else if (level == 3) parentId = cityId
        else if (level == 4) parentId = cabangId

        const props: TwirpProps = {
            url: Const.API_ListMedalStats,
            headers: { Authorization: token },
            timeout: 10000,
            data: { level: level, parent_id: parentId }
        }
        const result = await Twirp(props)
        if (result.state == 2) {
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        } else {
            const raw = result.data.Stats || []
            setStats(raw.map((v: any) => ({
                id: +v.id,
                name: v.name,
                gold_count: +v.gold_count,
                silver_count: +v.silver_count,
                bronze_count: +v.bronze_count,
            })))
        }
        setLoading(false)
    }

    useEffect(() => {
        loadStats()
    }, [level, countryId, stateId, cityId, cabangId])

    const drillDown = (s: MedalStat) => {
        if (level == 0) {
            setCountryId(s.id)
            setCountryName(s.name)
            setLevel(1)
        } else if (level == 1) {
            setStateId(s.id)
            setStateName(s.name)
            setLevel(2)
        } else if (level == 2) {
            setCityId(s.id)
            setCityName(s.name)
            setLevel(3)
        } else if (level == 3) {
            setCabangId(s.id)
            setCabangName(s.name)
            setLevel(4)
        }
    }

    const goBackTo = (targetLevel: number) => {
        setLevel(targetLevel)
    }

    const currentLevelName = t(LEVEL_NAMES[level])

    const totals = stats.reduce((acc, s) => ({
        gold: acc.gold + s.gold_count,
        silver: acc.silver + s.silver_count,
        bronze: acc.bronze + s.bronze_count,
    }), { gold: 0, silver: 0, bronze: 0 })

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={"10px"}>
                <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => goBackTo(0)} color={level == 0 ? "blue.500" : "gray.500"}>{t('Country')}</BreadcrumbLink>
                </BreadcrumbItem>
                {level >= 1 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(1)} color={level == 1 ? "blue.500" : "gray.500"}>{countryName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 2 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(2)} color={level == 2 ? "blue.500" : "gray.500"}>{stateName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 3 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(3)} color={level == 3 ? "blue.500" : "gray.500"}>{cityName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 4 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink color="blue.500">{cabangName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
            </Breadcrumb>

            <Text fontSize='md' fontWeight={'bold'} mb={"10px"}>{currentLevelName}</Text>

            {loading ? (
                <Center py={"40px"}><Spinner size='lg' /></Center>
            ) : stats.length === 0 ? (
                <Center py={"40px"}><Text fontSize='sm'>{t('no_records')}</Text></Center>
            ) : (
                <>
                    {/* Summary cards */}
                    <SimpleGrid columns={[2, 5]} spacing={4} mb={"20px"}>
                        <Stat bg="teal.50" p={3} borderRadius="md">
                            <StatLabel color="teal.600">{currentLevelName}</StatLabel>
                            <StatNumber>{stats.length}</StatNumber>
                        </Stat>
                        <Stat bg="yellow.50" p={3} borderRadius="md">
                            <StatLabel color="yellow.600">{t('Gold')}</StatLabel>
                            <StatNumber>{totals.gold}</StatNumber>
                        </Stat>
                        <Stat bg="gray.100" p={3} borderRadius="md">
                            <StatLabel color="gray.600">{t('Silver')}</StatLabel>
                            <StatNumber>{totals.silver}</StatNumber>
                        </Stat>
                        <Stat bg="orange.50" p={3} borderRadius="md">
                            <StatLabel color="orange.600">{t('Bronze')}</StatLabel>
                            <StatNumber>{totals.bronze}</StatNumber>
                        </Stat>
                        <Stat bg="blue.50" p={3} borderRadius="md">
                            <StatLabel color="blue.600">{t('Total')}</StatLabel>
                            <StatNumber>{totals.gold + totals.silver + totals.bronze}</StatNumber>
                        </Stat>
                    </SimpleGrid>

                    {/* Detail table */}
                    <TableContainer>
                        <Table variant='simple' colorScheme='blue' size='sm'>
                            <Thead>
                                <Tr>
                                    <Th>{t('Name')}</Th>
                                    <Th isNumeric>{t('Gold')}</Th>
                                    <Th isNumeric>{t('Silver')}</Th>
                                    <Th isNumeric>{t('Bronze')}</Th>
                                    <Th isNumeric>{t('Total')}</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {stats.map((s) => (
                                    <Tr key={s.id}>
                                        <Td fontWeight='bold'>
                                            {level == 4 ? (
                                                <Link href={"/rantingmedals/" + s.id} target='_blank' rel='noreferrer' color='blue.500'>
                                                    {s.name}
                                                </Link>
                                            ) : (
                                                <Button variant='link' colorScheme='blue' size='sm' onClick={() => drillDown(s)}>
                                                    {s.name}
                                                </Button>
                                            )}
                                        </Td>
                                        <Td isNumeric color='yellow.600' fontWeight='bold'>{s.gold_count}</Td>
                                        <Td isNumeric color='gray.500' fontWeight='bold'>{s.silver_count}</Td>
                                        <Td isNumeric color='orange.500' fontWeight='bold'>{s.bronze_count}</Td>
                                        <Td isNumeric fontWeight='bold'>{s.gold_count + s.silver_count + s.bronze_count}</Td>
                                    </Tr>
                                ))}
                                <Tr bg="gray.50" fontWeight='bold'>
                                    <Td>{t('Total')}</Td>
                                    <Td isNumeric color='yellow.600'>{totals.gold}</Td>
                                    <Td isNumeric color='gray.500'>{totals.silver}</Td>
                                    <Td isNumeric color='orange.500'>{totals.bronze}</Td>
                                    <Td isNumeric>{totals.gold + totals.silver + totals.bronze}</Td>
                                </Tr>
                            </Tbody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    )
}
